import React, { useCallback, useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import {
  CreateRebatePolicies,
  RebatePoliciesDto,
} from '@/api/Trading-Accounts/Types/RebatePoliciesDto'
import { rebatePoliciesColumns } from '@/routes/Transactions/rebatePoliciesColumns'
import { RebatePoliciesForm } from '@/routes/Transactions/RebatePoliciesForm'
import { PartnershipsApiClient } from '@/utils/api/partnershipsApi'
import { AuthSessionContext } from '@/utils/AuthContext'
import { hasPermission } from '@/utils/permissionsValidator'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState } from 'features/DataGrid'
import { useGridStatusState } from 'features/DataGrid/hooks/useGridStatusState'
import { isGoBack } from 'lib/react-router-dom'
import { useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
})

const useRebatePoliciesFetch = () => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [rebatePolicies, setRebatePolicies] = useState<RebatePoliciesDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { tickmillCompanies } = useMapStateToProps()

  useEffect(() => {
    fetchRebatePoliciesFetch()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRebatePoliciesFetch = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<RebatePoliciesDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await partnershipsApiClient.getRebatePolicies({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setRebatePolicies(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<RebatePoliciesDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  return {
    tickmillCompanies,
    rebatePolicies,
    setRebatePolicies,
    gridState,
    gridActions,
    tableData,
    setTableData,
    setGridFilters: gridActions.setGridFilters,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
    fetchRebatePoliciesFetch,
  }
}

const useRebatePoliciesMutate = () => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const setToast = useContext(ToastContext)

  const handleAdd = async (data: CreateRebatePolicies) => {
    await partnershipsApiClient.createRebatePolicies(data)
    setToast(successToast(`Rebate Policies added successfully.`))
  }

  const handleEdit = async (id: string, data: CreateRebatePolicies) => {
    await partnershipsApiClient.updateRebatePolicies(id, data)
    setToast(successToast(`Rebate Policies updated successfully.`))
  }

  const handleRemove = async (data: RebatePoliciesDto) => {
    await partnershipsApiClient.deleteRebatePolicies(data.id)
    setToast(successToast(`Rebate Policies removed successfully.`))
  }

  return { handleAdd, handleEdit, handleRemove }
}

const useRebatePoliciesHandlers = (props: ReturnType<typeof useRebatePoliciesFetch>) => {
  const { gridState, gridActions } = props
  const { getQuery, getSortQuery, setRebatePolicies, tableData, setTableData } = props

  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await partnershipsApiClient.getRebatePolicies({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setRebatePolicies(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSortQuery(event.sort)
    const response = await partnershipsApiClient.getRebatePolicies({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setRebatePolicies(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await partnershipsApiClient.getRebatePolicies({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setRebatePolicies(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await partnershipsApiClient.getRebatePolicies({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults
    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setRebatePolicies(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return {
    handleSkipCountChange,
    handleGridPageChange,
    handlePageSizeChange,
    handleSortChange,
  }
}

interface ModalProps {
  data: RebatePoliciesDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useRebatePoliciesModal = () => {
  const [rebatePoliciesModal, setRebatePoliciesModal] = useState<ModalProps>(initialModalState)

  const handleRebatePoliciesModalOpen = (data?: RebatePoliciesDto) => {
    setRebatePoliciesModal({ data, visible: true })
  }

  const handleRebatePoliciesModalClose = () => {
    setRebatePoliciesModal(initialModalState)
  }

  return {
    rebatePoliciesModal,
    handleRebatePoliciesModalOpen,
    handleRebatePoliciesModalClose,
  }
}

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = (data?: RebatePoliciesDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }

  const handleConfirmationModalClose = () => {
    setConfirmationModal(initialModalState)
  }

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const RebatePoliciesTable = () => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const rebatePoliciesFetch = useRebatePoliciesFetch()
  const { gridState, gridStatusState, tableData, setTableData, rebatePolicies, tickmillCompanies } =
    rebatePoliciesFetch

  const { rebatePoliciesModal, handleRebatePoliciesModalOpen, handleRebatePoliciesModalClose } =
    useRebatePoliciesModal()
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const handlers = useRebatePoliciesHandlers(rebatePoliciesFetch)
  const mutate = useRebatePoliciesMutate()
  const { handleSortChange, handleGridPageChange, handlePageSizeChange, handleSkipCountChange } =
    handlers

  const [isEdit, setIsEdit] = React.useState(false)

  const handleSubmit = async (id: string, data: CreateRebatePolicies) => {
    if (isEdit) {
      await mutate.handleEdit(id, data)
    } else {
      await mutate.handleAdd(data)
    }

    await rebatePoliciesFetch.fetchRebatePoliciesFetch()
    setIsEdit(false)
    handleRebatePoliciesModalClose()
  }

  const handleRemoveRebatePolicies = async (data?: RebatePoliciesDto) => {
    if (data?.id) {
      await mutate.handleRemove(data)
      await rebatePoliciesFetch.fetchRebatePoliciesFetch()
      handleConfirmationModalClose()
    }
  }

  const isEditRebatePoliciesAllowed = hasPermission(
    permissions,
    PermissionsEnum.CanEditRebatePolicies
  )

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <section>
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemoveRebatePolicies(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this IB rebate policy?</p>
            </div>
          }
        />
      )}
      {rebatePoliciesModal.visible && (
        <RebatePoliciesForm
          isOpen={rebatePoliciesModal.visible}
          onClose={handleRebatePoliciesModalClose}
          data={rebatePoliciesModal.data}
          tickmillCompanies={tickmillCompanies}
          onSubmit={(data) =>
            handleSubmit(rebatePoliciesModal.data?.id as string, data as CreateRebatePolicies)
          }
        />
      )}

      <AddButton
        text='Create Rebate Policies'
        onClick={handleRebatePoliciesModalOpen}
        disabled={!isEditRebatePoliciesAllowed}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={rebatePolicies}
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          {rebatePoliciesColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <GridColumn key={idx + 2} {...column} />
            ))}
          <GridColumn
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Delete',
                      onClick: () => handleConfirmationModalOpen(gridCellProps.dataItem),
                      disabled: !isEditRebatePoliciesAllowed,
                    },
                    {
                      name: 'Edit',
                      onClick: () => {
                        setIsEdit(true)
                        handleRebatePoliciesModalOpen(gridCellProps.dataItem)
                      },
                      disabled: !isEditRebatePoliciesAllowed,
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </section>
  )
}

export default RebatePoliciesTable
